import { useEffect } from 'react';
import { Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { CNY, USD, convert, formatCNY, formatMoney, toCNY } from '@/domain/fx';
import { useFxStore } from '@/store/fx';

const { Text } = Typography;

/**
 * 「≈ $xxx · ≈ ¥xxx」。用在**正在输入**金额的地方（新建打款申请的金额框下面）：
 * 那一刻还没有任何记录可以对照，申请人只能靠这两个熟悉的币种判断自己有没有多打一个零。
 *
 * 与 CnyHint 一样，这里的数字是参考值，样式必须次要。给的是美元和人民币两个锚点，
 * 与收款人币种相同的那个会自动略去。
 */
export function ConversionHint({ amount, currency }: { amount: string; currency: string | null }) {
  const rates = useFxStore((s) => s.rates);
  const fetchedAt = useFxStore((s) => s.fetchedAt);
  const load = useFxStore((s) => s.load);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  if (!currency) return null;

  const parts = [USD, CNY]
    .map((target) => ({ target, value: convert(amount, currency, target, rates) }))
    .filter((p): p is { target: string; value: number } => p.value !== null)
    .map((p) => `≈ ${formatMoney(p.value, p.target)}`);

  if (parts.length === 0) return null;

  return (
    <Tooltip title={hintTitle(fetchedAt)}>
      <Text
        type="secondary"
        style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', cursor: 'help' }}
      >
        {parts.join('  ·  ')}（参考）
      </Text>
    </Tooltip>
  );
}

/**
 * 只渲染「≈ ¥xxx」那一行。用在主金额已经由调用方自己排版的地方（比如打款确认弹窗里
 * 那个放大的金额），避免把金额显示两遍。
 */
export function CnyHint({ amount, currency }: { amount: string; currency: string }) {
  const rates = useFxStore((s) => s.rates);
  const fetchedAt = useFxStore((s) => s.fetchedAt);
  const load = useFxStore((s) => s.load);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  const cny = toCNY(amount, currency, rates);
  if (cny === null) return null;

  return (
    <Tooltip title={hintTitle(fetchedAt)}>
      <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', cursor: 'help' }}>
        ≈ {formatCNY(cny)}（参考）
      </Text>
    </Tooltip>
  );
}

/** 几处共用的提示文案。汇率取数时间要让人看得到，否则「参考」二字没有分量。 */
function hintTitle(fetchedAt: string | null): string {
  return fetchedAt
    ? `按 ${dayjs(fetchedAt).format('MM-DD HH:mm')} 的公开汇率估算，仅供参考；实际换汇以 Airwallex 成交汇率为准`
    : '仅供参考；实际换汇以 Airwallex 成交汇率为准';
}

/**
 * 金额 + 人民币参考值。非人民币币种才显示第二行。
 *
 * 🔴 人民币那一行必须**一眼看出是参考值**：更小的字号、次要色、前缀「≈」。
 * Airwallex 换汇用的是它自己在成交那一刻的汇率，这里的第三方公开汇率必然有差 ——
 * 让人误以为这是实付金额，会在对账时变成一堆说不清的差额。
 * 所以它不加粗、不与主金额同级，并且 hover 能看到汇率的取数时间。
 */
export default function AmountWithCNY({
  amount,
  currency,
  align = 'right',
}: {
  amount: string;
  currency: string;
  align?: 'left' | 'right';
}) {
  const rates = useFxStore((s) => s.rates);
  const fetchedAt = useFxStore((s) => s.fetchedAt);
  const load = useFxStore((s) => s.load);

  useEffect(() => {
    // store 内部有「只取一次」的保护，这里可以放心每次挂载都调
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  const cny = toCNY(amount, currency, rates);

  return (
    <div style={{ textAlign: align, lineHeight: 1.35 }}>
      {/* 等宽数字：金额列对齐后，多一位少一位一眼能看出来 */}
      <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
        {amount} {currency}
      </Text>
      {cny !== null && (
        <div>
          <Tooltip title={hintTitle(fetchedAt)}>
            <Text
              type="secondary"
              style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', cursor: 'help' }}
            >
              ≈ {formatCNY(cny)}
            </Text>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
